import Button from "@mui/material/Button"
import Divider from "@mui/material/Divider"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useCallback, useEffect, useState } from "react"

import { parseApiErrorMessage } from "../../../common/apiErrors"
import { useAuthorizedAxiosClient } from "../../../common/axios"
import CloseableSnackbar, {
  useCloseableSnackbarState,
} from "../../../common/components/CloseableSnackbar"
import Column from "../../../common/components/Column"
import ConditionalPurpleLinearProgress from "../../../common/components/ConditionalLinearProgress"
import Row from "../../../common/components/Row"
import SettingsSectionCard from "./SettingsSectionCard"
import { TeamFeatureBanner } from "./TeamFeatureHighlight"

const MAX_MEMBERS = 5

export default function TeamTab() {
  const axiosClient = useAuthorizedAxiosClient()
  const [isLoading, setIsLoading] = useState(true)
  const [overview, setOverview] = useState({
    owner: null,
    members: [],
    pending_invitations: [],
  })
  const [email, setEmail] = useState("")
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [inviteError, setInviteError] = useState(null)
  const [resendError, setResendError] = useState(null)
  const [resendingInvitationId, setResendingInvitationId] = useState(null)
  const {
    isOpen: isSnackbarOpen,
    message: snackbarMessage,
    severity: snackbarSeverity,
    open: openSnackbar,
    openError: openErrorSnackbar,
    close: closeSnackbar,
  } = useCloseableSnackbarState()

  const loadOverview = useCallback(() => {
    setIsLoading(true)
    axiosClient
      .get("/clients/api/team/")
      .then(({ data }) => setOverview(data))
      .catch(({ response }) => {
        openErrorSnackbar(
          parseApiErrorMessage(response?.data, "Could not load team settings")
        )
      })
      .finally(() => setIsLoading(false))
  }, [axiosClient, openErrorSnackbar])

  useEffect(() => {
    loadOverview()
    // Load once when the Team tab mounts; loadOverview is also called after mutations.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const activeMemberCount = overview.members.filter(
    (m) => m.role === "member"
  ).length
  const atMemberLimit = activeMemberCount >= MAX_MEMBERS

  function handleInvite() {
    setInviteError(null)
    axiosClient
      .post("/clients/api/team/invitations/", {
        email: email.toLowerCase().trim(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      })
      .then(() => {
        openSnackbar(`Invitation email sent to ${email}`)
        setEmail("")
        setFirstName("")
        setLastName("")
        loadOverview()
      })
      .catch(({ response }) => {
        const message = parseApiErrorMessage(response?.data, "Invite failed")
        setInviteError(message)
        openErrorSnackbar(message)
      })
  }

  function handleRevokeMember(membershipId) {
    axiosClient
      .delete(`/clients/api/team/members/${membershipId}/`)
      .then(() => loadOverview())
      .catch(({ response }) => {
        openErrorSnackbar(
          parseApiErrorMessage(response?.data, "Could not remove team member")
        )
      })
  }

  function handleRevokeInvitation(invitationId) {
    axiosClient
      .delete(`/clients/api/team/invitations/${invitationId}/`)
      .then(() => {
        setResendError(null)
        loadOverview()
      })
      .catch(({ response }) => {
        openErrorSnackbar(
          parseApiErrorMessage(response?.data, "Could not revoke invitation")
        )
      })
  }

  function handleResendInvitation(invitationId) {
    setResendError(null)
    setResendingInvitationId(invitationId)
    axiosClient
      .post(`/clients/api/team/invitations/${invitationId}/resend/`)
      .then(() => {
        setResendError(null)
        openSnackbar("Invitation resent")
        loadOverview()
      })
      .catch(({ response }) => {
        const message = parseApiErrorMessage(
          response?.data,
          "Could not resend invitation"
        )
        setResendError(message)
        openErrorSnackbar(message)
      })
      .finally(() => setResendingInvitationId(null))
  }

  return (
    <Column sx={{ gap: 3, alignItems: "stretch", maxWidth: 720 }}>
      <ConditionalPurpleLinearProgress visible={isLoading} />

      <SettingsSectionCard sx={{ gap: 2.5 }}>
        <TeamFeatureBanner
          activeMemberCount={isLoading ? 0 : activeMemberCount}
        />

        <Divider />

        <Column sx={{ gap: 2 }}>
          <Typography variant="h6">Invite team member</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>
            {activeMemberCount} of {MAX_MEMBERS} team members
          </Typography>
          <TextField
            label="Email"
            size="small"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
          />
          <Row sx={{ gap: 2 }}>
            <TextField
              label="First name"
              size="small"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              fullWidth
            />
            <TextField
              label="Last name"
              size="small"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              fullWidth
            />
          </Row>
          {inviteError && (
            <Typography color="error" variant="body2">
              {inviteError}
            </Typography>
          )}
          <Row sx={{ justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              onClick={handleInvite}
              disabled={!email || !firstName || atMemberLimit}
            >
              Send invitation
            </Button>
          </Row>
        </Column>

        <Divider />

        <Column sx={{ gap: 1 }}>
          <Typography variant="h6">Team members</Typography>
          {overview.members
            .filter((m) => m.role !== "owner")
            .map((member) => (
              <Row
                key={member.id}
                sx={{ justifyContent: "space-between", alignItems: "center" }}
              >
                <Typography>
                  {member.name} ({member.email})
                  {member.role === "executive_caller" && " — SmartSetter EC"}
                </Typography>
                {member.role === "member" && (
                  <Button
                    size="small"
                    color="error"
                    onClick={() => handleRevokeMember(member.id)}
                  >
                    Remove
                  </Button>
                )}
              </Row>
            ))}
        </Column>

        <Divider />

        <Column sx={{ gap: 1 }}>
          <Typography variant="h6">Pending invitations</Typography>
          {overview.pending_invitations.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No pending invitations
            </Typography>
          )}
          {resendError && (
            <Typography color="error" variant="body2">
              {resendError}
            </Typography>
          )}
          {overview.pending_invitations.map((invite) => (
            <Row
              key={invite.id}
              sx={{ justifyContent: "space-between", alignItems: "center" }}
            >
              <Typography>
                {invite.first_name} {invite.last_name} ({invite.email})
              </Typography>
              <Row sx={{ gap: 1 }}>
                <Button
                  size="small"
                  onClick={() => handleResendInvitation(invite.id)}
                  disabled={resendingInvitationId === invite.id}
                >
                  {resendingInvitationId === invite.id
                    ? "Sending..."
                    : "Resend"}
                </Button>
                <Button
                  size="small"
                  color="error"
                  onClick={() => handleRevokeInvitation(invite.id)}
                >
                  Revoke
                </Button>
              </Row>
            </Row>
          ))}
        </Column>
      </SettingsSectionCard>

      <CloseableSnackbar
        open={isSnackbarOpen}
        onClose={closeSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Column>
  )
}
